/** The nav items this user may see, grouped for rendering. */
import { useCallback, useMemo } from "react";
import { useCan } from "@features/auth/hooks/useCan";
import { useModule } from "@features/auth/hooks/useModule";
import { useAuth } from "@features/auth/AuthContext";
import {
  NAV_ITEMS, NAV_SCOPES, NAV_TOPICS,
  type NavItem, type ScopeId, type TopicId,
} from "@app/navigation/nav";

export interface ScopeSection {
  scope: (typeof NAV_SCOPES)[number];
  topics: Array<{ topic: TopicId; label: string; icon: string; items: NavItem[] }>;
  count: number;
}

const SCOPE_RANK: Record<string, number> = { platform: 3, group: 2, org: 1 };

export function useNav() {
  const { can } = useCan();
  const { hasModule } = useModule();
  const { user } = useAuth();
  const userRank = SCOPE_RANK[user?.scope_level ?? "org"] ?? 1;

  const visible = useMemo(
    () => NAV_ITEMS.filter((i) => {
      if (i.perm && !can(i.perm)) return false;
      if (i.scopeLevel && (SCOPE_RANK[i.scopeLevel] ?? 0) > userRank) return false;
      // exactScope: broader scopes do NOT inherit these items
      if (i.exactScope && user?.scope_level !== i.exactScope) return false;
      // Variation A: a disabled module's items vanish entirely (handoff §2).
      if (i.module && !hasModule(i.module)) return false;
      return true;
    }),
    [can, hasModule, userRank, user?.scope_level],
  );

  const sections = useMemo<ScopeSection[]>(() => {
    return NAV_SCOPES.map((scope) => {
      const inScope = visible.filter((i) => i.scope === scope.id);
      const topics = NAV_TOPICS.map((t) => ({
        topic: t.id,
        label: t.label,
        icon: t.icon,
        items: inScope.filter((i) => i.topic === t.id),
      })).filter((t) => t.items.length > 0);
      return { scope, topics, count: inScope.length };
    }).filter((s) => s.count > 0);
  }, [visible]);

  /** Find the item matching a pathname, for breadcrumbs / active state.
   *  Kept as a stable callback so consumers (AppShell title, Sidebar
   *  active state) can memoize on it without re-computing every tick. */
  const itemFor = useCallback(
    (pathname: string): NavItem | undefined =>
      visible.find((i) => {
        if (pathname === i.to || pathname.startsWith(`${i.to}/`)) return true;
        // Parameterised screens that belong to a landing-page nav entry.
        return i.altMatch !== undefined && new RegExp(i.altMatch).test(pathname);
      }),
    [visible],
  );

  const scopeFor = (id: ScopeId) => NAV_SCOPES.find((s) => s.id === id);

  return { visible, sections, itemFor, scopeFor };
}
